/**
 * insertAttributes — ATTRIB / ATTDEF → `text-run` resolution for the PDF plot
 * INSERT converter (S10-PDF-RECON, PDF-F1..F5).
 *
 * Split out of `insertConverter.ts` (CLAUDE.md file-size guardrail) — it owns
 * the Phase A / B attribute resolution `walkBlock` drives per INSERT level.
 * The OWNING INSERT's `attributes[]` are matched against the block definition's
 * ATTDEFs BY TAG + occurrence, mirroring the typed reference oracle
 * `scene3d/adapters/typed/{attdefFromEntity,blockFromEntity}.ts`:
 *   - a VARIABLE ATTDEF renders the matching ATTRIB row's value, falling back
 *     to the ATTDEF `defaultValue` (never blank) when no row matches (PDF-F3);
 *   - a CONSTANT ATTDEF renders its own `defaultValue` at every INSERT (PDF-F2);
 *   - duplicate-tag rows match Nth ATTRIB → Nth ATTDEF by occurrence (PDF-F4);
 *   - a WORLD `overridePosition` ATTRIB rides only the OUTER residual
 *     (`AttrEmitContext.outer`), NOT the ATTRIB's own INSERT affine (PDF-F1 /
 *     PDF-F5 — no double-transform);
 *   - a block-LOCAL (unset) ATTRIB/ATTDEF rides the full per-level `baseMatrix`.
 *
 * `walkBlock` passes its full `InsertWalkState`, which structurally satisfies
 * the narrower {@link AttrEmitContext} (the exact fields these functions read).
 * The context type is defined HERE, not imported from `insertConverter.ts`, so
 * this leaf module never imports its only consumer (dependency-cruiser
 * zero-cycle gate; CLAUDE.md ПРИОРИТЕТ #2).
 */

import type {
  AttdefEntity,
  BlockInsertAttribute,
} from "@dxf-ai-editor/drawing-model";
import {
  TEXT_GEN_FLAG_BACKWARD,
  TEXT_GEN_FLAG_UPSIDE_DOWN,
  rotateLineStackOffset,
} from "@dxf-ai-editor/command-engine";

import type {
  CadConvertContext,
  CadPrimitive,
  PlotStyle,
  Point2,
} from "../cadPrimitive";
import { resolvePlotRgb, type RgbColor } from "../pdfTransform";

import { apply, applyLinear, type Affine2D } from "./affine2d";
import { resolvePlotStyle } from "./resolveStyle";

/**
 * The subset of `insertConverter.ts InsertWalkState` the ATTRIB / ATTDEF
 * resolution reads. `InsertWalkState` carries all of these (plus the recursion
 * matrix / visited / depth / instance the geometry walk needs), so the caller
 * passes its `state` directly — structural typing keeps the two in sync without
 * a reverse import into the consumer module.
 */
export interface AttrEmitContext {
  /** Conversion context — used only for its `layerColors` here. */
  ctx: CadConvertContext;
  /** Owning top-level INSERT id (stamped as every text-run's `entityId`). */
  parentId: string;
  /** The owning INSERT's OWN resolved colour hex — attribute text inherits it
   *  (an ATTRIB carries no independent colour in the model). */
  blockColor: string | null;
  /** The owning INSERT's ACI (1..256) for a BYBLOCK ATTDEF's CTB lookup, or
   *  null when it cannot be determined (PDF-F16). A ByBlock template colour has
   *  no index of its own, so its do-not-plot / screening CTB entry follows the
   *  enclosing INSERT's ACI (`blockColor` already collapsed that ACI to a hex). */
  blockAci: number | null;
  /** The owning INSERT's resolved OCG layer — attribute text files under it. */
  insertLayer: string;
  /** The owning INSERT's `attributes[]` (Phase A tag/occurrence match). */
  attributes: readonly BlockInsertAttribute[];
  /** The OUTER residual for a WORLD `overridePosition` (PDF-F1 / PDF-F5). */
  outer: Affine2D;
  /**
   * PDF-F10 — the accumulated AUTHORED INSERT-rotation SUM (deg) for the
   * transform a block-LOCAL attribute text rides (`baseMatrix`), carried from
   * the walk (the oracle's `t.rotationDeg`). Threaded — NOT inferred from the
   * composed matrix — because a composed single-axis mirror is ambiguous (see
   * {@link foldScalarRotationDeg}).
   */
  rotationSumDeg: number;
  /** PDF-F10 — sign of the accumulated authored scaleX product (`t.scale.x<0`). */
  scaleXNeg: boolean;
  /** PDF-F10 — sign of the accumulated authored scaleY product (`t.scale.y<0`). */
  scaleYNeg: boolean;
  /**
   * PDF-F10 — the folded scalar rotation (deg) of the OUTER residual
   * (`state.outer`) a WORLD `overridePosition` rides: 0 for a top-level /
   * MINSERT residual (identity / pure translation), the ENCLOSING block's
   * scalar rotation for a nested INSERT. Threaded (not inferred from
   * `state.outer`) for the same mirror-ambiguity reason.
   */
  outerScalarRotationDeg: number;
}

/**
 * Read an ATTDEF group-70 flag bit (`constant` bit 2 / `invisible` bit 1)
 * defensively. The PDF converter consumes `ctx.blocks`, normally schema-parsed
 * (so the decomposed `flags` bag is always present), but a caller / test may
 * hand-build a block definition WITHOUT `flags`; treat a missing bag as
 * "all bits clear".
 */
export function attdefFlag(
  attdef: AttdefEntity,
  bit: "constant" | "invisible",
): boolean {
  const flags = attdef.flags as
    | { constant?: boolean; invisible?: boolean }
    | undefined;
  return flags?.[bit] === true;
}

/**
 * GitHub Codex review PR #558 round 2 (round-3 local read-only follow-up
 * P2, generalised by the round-9 GitHub bot follow-up P2) — decode a
 * resolved `generationFlags` bitfield into the `TextRunPrimitive.mirrored`
 * (bit 4, `TEXT_GEN_FLAG_UPSIDE_DOWN`, local-Y) / `.backward` (bit 2,
 * `TEXT_GEN_FLAG_BACKWARD`, local-X) booleans — the SAME 2-bit decode
 * `textGeometry.ts` uses for the live WebGL "text"-kind renderer
 * (`applyMirrorScale`'s independent `sx`/`sy` local-axis flips).
 *
 * A block ATTRIB's MIRRTEXT=1 mirror (`geometryReducerEdit.ts`'s
 * `withWorldAttributeGenerationFlags`) XOR-toggles ONLY bit 4 — matching
 * the SAME local-Y-flip `mirrored` convention `DimensionPrimitive`/
 * `MultileaderPrimitive`'s own `mirrored` field already uses. But an
 * IMPORTED DXF's `overrideGenerationFlags` can independently carry bit 2
 * (`backward`) from the source file, unrelated to THIS campaign's own
 * MIRRTEXT mirror — round-3's original decode dropped it entirely
 * (reducing to bit-4-only), so an imported backward-flipped WORLD ATTRIB
 * (or one that becomes bit-2+bit-4 combined after a MIRRTEXT mirror, e.g.
 * 2→6) printed PDF glyphs missing the backward axis, a DIFFERENT
 * orientation than the viewport/DXF. Both bits now decode independently,
 * matching the render oracle exactly.
 */
function attributeHandedness(
  generationFlags: number,
): { mirrored: boolean; backward: boolean } {
  return {
    mirrored: (generationFlags & TEXT_GEN_FLAG_UPSIDE_DOWN) !== 0,
    backward: (generationFlags & TEXT_GEN_FLAG_BACKWARD) !== 0,
  };
}

/**
 * AutoCAD block-child layer resolution (PDF-6.2a). A child entity authored on
 * layer "0" inherits the enclosing INSERT's resolved layer; any other layer is
 * kept verbatim — the canonical rule mirrored by `state/blockReducerHelpers.ts
 * resolveChildLayer` (EXPLODE reducer) and the typed oracle
 * `scene3d/adapters/typed/blockTransform.ts resolveBlockChildLayer` (which
 * `attdefFromEntity` feeds through `transformBlockChild`).
 *
 * Defined in THIS leaf (rather than `insertConverter.ts`) so both the ATTDEF /
 * ATTRIB emitters HERE and the geometry / TEXT walker in the consumer share ONE
 * truth without the leaf importing its only consumer (dependency-cruiser
 * zero-cycle gate; CLAUDE.md ПРИОРИТЕТ #2). A MATCHED ATTDEF template DOES carry
 * its own OCG layer (`AttdefEntity.layer`, DXF group 8), so it must ride this
 * rule exactly like a standalone block-child TEXT (`insertConverter.ts
 * textChildToRun`) — NOT be forced onto the INSERT's layer.
 */
export function resolveChildLayer(
  childLayer: string,
  insertLayer: string,
): string {
  return childLayer === "0" ? insertLayer : childLayer;
}

/**
 * DXF horizontal (group 72) / vertical (group 74) justification: any nonzero
 * value makes the ATTDEF's visual anchor the alignment point (11/21), not the
 * insertion point (10/20) — Autodesk DXF reference (TEXT/ATTDEF-shared rule);
 * twin of `textConverter.ts usesAlignmentPoint` + `attdefFromEntity.ts`.
 */
function usesAlignmentPoint(hJust: number, vJust: number): boolean {
  return hJust !== 0 || vJust !== 0;
}

/**
 * Resolve an ATTDEF template's OWN block-local anchor: the alignment point
 * (11/21) for a justified template that carries one, the insertion point
 * (10/20) otherwise — mirroring `attdefFromEntity.ts resolveTemplateAnchor`
 * and the export twin `resolveAttdefTemplateAnchorForExport`.
 */
function resolveTemplateAnchor(attdef: AttdefEntity): Point2 {
  return usesAlignmentPoint(attdef.hJustification, attdef.vJustification) &&
    attdef.alignmentPoint !== undefined
    ? { x: attdef.alignmentPoint.x, y: attdef.alignmentPoint.y }
    : { x: attdef.insertionPoint.x, y: attdef.insertionPoint.y };
}

/**
 * PDF-F4 — resolve the ATTRIB row matching an ATTDEF template by TAG +
 * occurrence (the only key `blockInsertAttributeSchema` carries — no ATTDEF
 * back-pointer). A block with N same-tag variable ATTDEFs gets N same-tag
 * ATTRIB rows on a real INSERT, matched positionally (`ezdxf add_auto_attribs`
 * walks the ATTDEFs in order); a plain `.find()` returned the FIRST row for
 * every occurrence so later duplicate-tag rows stacked onto the first
 * template's anchor. `occurrenceIndex` selects the Nth row (byte-identical to
 * `.find()` for the common unique-tag case, where the index is always 0).
 */
function findAttrByTagOccurrence(
  attributes: readonly BlockInsertAttribute[],
  tag: string,
  occurrenceIndex: number,
): BlockInsertAttribute | undefined {
  let seen = 0;
  for (const a of attributes) {
    if (a.tag !== tag) continue;
    if (seen === occurrenceIndex) return a;
    seen++;
  }
  return undefined;
}

/** |scaleY| of an affine's linear part (Y-axis image length) — the height
 *  scale a block-local / WORLD ATTRIB inherits from its transform (PDF-F1).
 *  Exported (PDF-TEXT-CHILD-GEOMETRY) — `insertTextChild.ts textChildToRun`
 *  reuses this SAME magnitude for a standalone block-child TEXT/MTEXT, so the
 *  "affine scale magnitude" stays ONE truth shared by both text emitters. */
export function affineScaleY(m: Affine2D): number {
  return Math.hypot(m.c, m.d);
}

/** |scaleX| of an affine's linear part (X-axis image length) — the width scale
 *  the glyph aspect (`widthFactor`) inherits (PDF-F9; `insertMatrix` builds the
 *  linear part as `[cos·sx, sin·sx; −sin·sy, cos·sy]`, so `hypot(a,b) = |sx|`).
 *  Exported (PDF-TEXT-CHILD-GEOMETRY) — see {@link affineScaleY}. */
export function affineScaleX(m: Affine2D): number {
  return Math.hypot(m.a, m.b);
}

/**
 * PDF-F10 — the mirror-aware SCALAR baseline rotation (degrees, CCW) an
 * attribute's text inherits, matching the typed reference oracle EXACTLY
 * (`blockTransform.ts transformBlockChild`'s `scalarRotationDeg` + the export
 * twin `dxfBridge.attribExportPosition.ts`'s `scalarRotationDegForExport`): a
 * DOUBLE-negative scale `S(−s,−s) ≡ R(180°)` (det > 0 — a point reflection, NOT
 * a mirror) folds an extra 180°; a single-axis TRUE mirror (det < 0) and the
 * plain positive case keep the RAW accumulated rotation sum.
 *
 * The value is CARRIED through the walk from the AUTHORED per-level
 * `rotation` / `scaleX` / `scaleY` (accumulated as a rotation SUM + scale-sign
 * products), NOT recovered from the composed affine. A composed single-axis
 * mirror is genuinely AMBIGUOUS: an X-mirror at θ and a Y-mirror at θ+180
 * produce the IDENTICAL matrix, for which the oracles — reading the authored
 * signs — give θ vs θ+180. The previous `atan2`-based inference picked one
 * "upright" reading, which diverged from the oracle whenever the authored
 * rotation left the readable half-turn: a `scaleX=−1, rotation=120` block
 * printed its attribute at −60° (upside-down) instead of the oracle's 120°.
 * Threading the authored sum reproduces the oracle for EVERY sign/rotation
 * combination.
 */
export function foldScalarRotationDeg(
  rotationSumDeg: number,
  scaleXNeg: boolean,
  scaleYNeg: boolean,
): number {
  return scaleXNeg && scaleYNeg ? rotationSumDeg + 180 : rotationSumDeg;
}

/**
 * AutoCAD's documented default MTEXT "at least" line pitch (5/3 × nominal
 * height) — mirrors the typed oracle `attdefFromEntity.ts LINE_PITCH_FACTOR`
 * so a multiline attribute value stacks identically in the PDF plot.
 */
const LINE_PITCH_FACTOR = 5 / 3;

/**
 * `rotateLineStackOffset` (the step-down offset for line `i` of a stacked
 * multiline value, `distance` units along the text's OWN LOCAL reading-down
 * direction, MIRRTEXT-gated) now lives in `@dxf-ai-editor/command-engine`
 * (`mirrorRotationField.ts`) — extracted (jscpd dup-gate) as the ONE shared
 * truth both this PDF converter and the typed scene3d oracle
 * (`attdefFromEntity.ts`) call, rather than two independently-maintained
 * near-identical copies.
 */

/** Resolved placement (world mm) of one attribute text line. */
interface AttrPlacement {
  position: Point2;
  height: number;
  rotation: number;
  /** PDF-F9 — glyph-aspect multiplier `|scaleX|/|scaleY|` of the effective
   *  transform, applied to the template `widthFactor` so the painter's
   *  `height × widthFactor` horizontal metric carries the X-axis scale (the
   *  height already carries `|scaleY|`). 1 under uniform scale (incl. mirror). */
  widthFactorScale: number;
  /**
   * PDF-multiline — the WORLD-space offset between consecutive stacked lines
   * (line `i` sits at `position + i · lineStepWorld`). Formed in the text's
   * LOCAL frame (LOCAL height × 5/3 pitch, LOCAL rotation) then mapped through
   * the effective transform's LINEAR part (`applyLinear`), exactly like the
   * oracle `attdefFromEntity.ts renderAttributeValueLines` builds the offset in
   * the ATTDEF frame and lets `transformBlockChild` apply the block affine. So
   * a non-uniformly scaled / rotated INSERT spaces lines by the correct
   * per-axis pitch — a plain `{x:0,-pitch}` in world space (final height, final
   * rotation) is only correct under uniform scale. `{x:0,y:-height·5/3}` for
   * the common unrotated uniform case; unused for a single-line value.
   */
  lineStepWorld: Point2;
}

/**
 * Resolve an attribute's placement under the correct transform regime.
 *
 * PDF-F1 / PDF-F5 — a WORLD placement (explicit `overridePosition`) rides ONLY
 * the OUTER residual (`state.outer`): IDENTITY for a top-level / base-cell
 * INSERT (the world point is already final — no double-transform), a pure
 * translation for a MINSERT non-base cell, the enclosing block's local→world
 * map for a nested INSERT. Height / rotation inherit that residual's
 * scale / rotation (a geometric no-op for the IDENTITY / pure-translation
 * cases, so a top-level / MINSERT world ATTRIB stays verbatim).
 *
 * A block-LOCAL placement rides the full per-level `baseMatrix` (basePoint
 * subtracted, same as every geometry child). Height / rotation / glyph aspect
 * ALSO ride that matrix's linear part — height · |scaleY| (`affineScaleY`),
 * rotation + the mirror-aware baseline rotation (`foldScalarRotationDeg`,
 * carried from the authored per-level rotation/scale, PDF-F10) and
 * widthFactor · |scaleX|/|scaleY| (`widthFactorScale`, PDF-F9) —
 * so a scaled / rotated / mirrored INSERT grows, turns and stretches its
 * block-local attribute text with the block, mirroring the typed oracle's
 * `transformBlockChild` (`height · |scaleY|`, `rotation + scalarRotationDeg`
 * with the round-6 double-negative fold, `widthFactor · |sx|/|sy|`). The world
 * and block-local branches are ONE formula over the effective transform (the
 * OUTER residual for a WORLD placement, the per-level `baseMatrix` otherwise),
 * so both regimes carry the same scalar conventions. Non-uniform-ROTATED shear
 * stays the shared S10-XFORM residual the typed oracle also defers (module
 * header of `attdefFromEntity.ts`). (PDF-TEXT-CHILD-GEOMETRY — the sibling
 * standalone block-child TEXT/MTEXT `insertTextChild.ts textChildToRun` now
 * rides the SAME `foldScalarRotationDeg` / `affineScaleX` / `affineScaleY`
 * composition for its height / rotation / widthFactor, closing what was a
 * distinct pre-existing PDF-geometry-text gap entangled with its own
 * MTEXT-rotation-drop — both fixed together, sharing this module's helpers.)
 */
function resolveAttrPlacement(
  worldPlacement: boolean,
  anchorLocal: Point2,
  heightBase: number,
  rotBase: number,
  baseMatrix: Affine2D,
  state: AttrEmitContext,
  // GitHub Codex review PR #558 round 2 (round-11 local read-only
  // follow-up) P2 — threaded so the multi-line stacking direction
  // (`lineStepWorld` below) reflects alongside the glyph mirror; see
  // `rotateLineStackOffset`'s own doc for the derivation.
  mirrored: boolean,
): AttrPlacement {
  // WORLD placement (`overridePosition`) rides ONLY the OUTER residual
  // (`state.outer`); a block-LOCAL placement rides the full per-level
  // `baseMatrix`. Position + scale magnitudes come from that effective matrix;
  // the SCALAR rotation is CARRIED from the authored per-level rotation/scale
  // (PDF-F10 — a composed single-axis mirror is ambiguous, so the matrix cannot
  // recover the oracle's angle; see `foldScalarRotationDeg`). Block-LOCAL text
  // rides the accumulated block transform's fold; a WORLD placement rides the
  // OUTER residual's fold (0 for the top-level / MINSERT identity-or-translation
  // case, the enclosing block's fold for a nested INSERT).
  const m = worldPlacement ? state.outer : baseMatrix;
  const scaleY = affineScaleY(m);
  const scalarRotationDeg = worldPlacement
    ? state.outerScalarRotationDeg
    : foldScalarRotationDeg(
        state.rotationSumDeg,
        state.scaleXNeg,
        state.scaleYNeg,
      );
  // PDF-multiline — build the per-line step in the text's LOCAL frame (LOCAL
  // height pitch, LOCAL `rotBase`) then map it through the effective transform's
  // LINEAR part, so a non-uniformly scaled / rotated INSERT spaces stacked lines
  // by the correct per-axis pitch (oracle `renderAttributeValueLines`). The
  // LOCAL pitch height falls back to 2.5 (matching the oracle's
  // `positiveOr(height, 2.5)`) so a 0-height multiline still steps line-by-line.
  const localPitch = heightBase > 0 ? heightBase : 2.5;
  const lineStepWorld = applyLinear(
    m,
    rotateLineStackOffset(localPitch * LINE_PITCH_FACTOR, rotBase, mirrored),
  );
  return {
    position: apply(m, anchorLocal),
    height: heightBase * scaleY,
    rotation: rotBase + scalarRotationDeg,
    // PDF-F9 — |scaleX|/|scaleY|; guarded against a degenerate zero-Y scale
    // (a 0-height text renders nothing anyway) so the run never carries a
    // NaN / Infinity widthFactor.
    widthFactorScale: scaleY === 0 ? 1 : affineScaleX(m) / scaleY,
    lineStepWorld,
  };
}

/** Text-style fields (beyond placement) a resolved attribute value needs. */
interface AttrStyleFields {
  widthFactor: number;
  obliqueAngle: number;
  hJustification: number;
  vJustification: number;
}

/** Style fields sourced from an ATTDEF template (matched / constant / default
 *  fallback), matching the prior `attributeToTextRun`'s `attdef?.*` reads. */
function attdefStyleFields(attdef: AttdefEntity): AttrStyleFields {
  return {
    widthFactor: attdef.widthFactor ?? 1,
    obliqueAngle: attdef.obliqueAngle ?? 0,
    hJustification: attdef.hJustification ?? 0,
    vJustification: attdef.vJustification ?? 0,
  };
}

/** Style fields for an ORPHAN ATTRIB (no ATTDEF template to inherit from) —
 *  the same left-justified defaults the prior `attributeToTextRun` used when
 *  it found no matching ATTDEF. */
const ORPHAN_STYLE_FIELDS: AttrStyleFields = {
  widthFactor: 1,
  obliqueAngle: 0,
  hJustification: 0,
  vJustification: 0,
};

/**
 * The colour an ORPHAN ATTRIB inherits — the OWNING INSERT's resolved colour.
 * An orphan has NO ATTDEF template to source a colour from (an ATTRIB carries
 * no independent colour in the model — `blockInsertAttributeSchema` has no
 * colour field), so it files under the INSERT's own resolved hex, mirroring the
 * typed orphan path (`orphanAttributeFromEntity` renders `color:{kind:"byBlock"}`,
 * which resolves to this same enclosing-INSERT colour). White → PLOT_BLACK via
 * `resolvePlotRgb`. A MATCHED / CONSTANT ATTDEF does NOT use this — it resolves
 * its OWN template colour via {@link resolveAttdefPlotStyle} (PDF-F11).
 */
function attributeColor(state: AttrEmitContext): RgbColor {
  return resolvePlotRgb(
    state.blockColor ?? undefined,
    state.insertLayer,
    state.ctx.layerColors,
    state.blockColor ?? null,
  );
}

/**
 * PDF-F11 / F12 — resolve a MATCHED / CONSTANT ATTDEF template's full plot style
 * through the BYBLOCK chain, exactly like `insertConverter.ts childStyle` does
 * for geometry children and `textConverter.ts textToPrimitives` for standalone
 * TEXT, and mirroring the typed oracle `attdefFromEntity.ts` (which feeds
 * `attdef.color` / `attdef.layer` through the shared common-property resolver).
 *
 * The ATTDEF carries its OWN colour (DXF group 62) + layer (group 8), so the
 * text must inherit the TEMPLATE colour (resolved against the ATTDEF's own
 * layer for ByLayer, or the enclosing INSERT for ByBlock) — NOT be forced onto
 * the owning INSERT's colour (PDF-F11: a constant ATTDEF on NOTES with a
 * ByLayer colour inside an INSERT on PLAN must take the NOTES colour). Only the
 * resolved colour, CTB/STB screening alpha and the do-not-plot `skip` flag
 * reach the text run (a glyph run carries no stroke lineweight / linetype), but
 * resolving the FULL style keeps the CTB/STB gate identical to every other text
 * converter (PDF-F12: a do-not-plot ATTDEF must emit nothing, like the other
 * converters short-circuiting on `style.skip`).
 */
function resolveAttdefPlotStyle(
  attdef: AttdefEntity,
  state: AttrEmitContext,
): PlotStyle {
  const ctx: CadConvertContext = {
    ...state.ctx,
    blockColor: state.blockColor,
    // PDF-F16 — a BYBLOCK template colour has no ACI of its own; carry the
    // enclosing INSERT's ACI so its do-not-plot / screening CTB entry applies
    // (otherwise `entityAciForCtb` reads the collapsed hex and skips the CTB).
    blockAci: state.blockAci,
  };
  // PDF-6.2a — the ATTDEF text FILES under the EFFECTIVE child layer
  // (`resolveChildLayer(attdef.layer, insertLayer)`: an ATTDEF authored on
  // layer "0" inherits the enclosing INSERT's resolved layer). Its ByLayer
  // colour AND CTB/STB do-not-plot lookup MUST resolve against that SAME
  // effective layer, not the literal authored "0" — otherwise a common layer-0
  // ATTDEF ignores the INSERT layer's colour / do-not-plot settings while the
  // run still lands on the INSERT layer (paint / filter vs OCG-layer mismatch).
  // A non-"0" template layer is unchanged (kept verbatim by resolveChildLayer).
  const effectiveLayer = resolveChildLayer(attdef.layer, state.insertLayer);
  return resolvePlotStyle({ ...attdef, layer: effectiveLayer }, ctx);
}

/** Build + record one attribute text-run primitive. The resolved `color` is
 *  passed by the caller (a MATCHED / CONSTANT ATTDEF's own template colour,
 *  PDF-F11; an ORPHAN ATTRIB's enclosing-INSERT colour), as is any CTB/STB
 *  screening `alpha` (PDF-F12). */
function pushAttrTextRun(
  out: CadPrimitive[],
  state: AttrEmitContext,
  placement: AttrPlacement,
  value: string,
  styleName: string,
  style: AttrStyleFields,
  layer: string,
  color: RgbColor,
  alpha?: number,
  mirrored?: boolean,
  backward?: boolean,
): void {
  // PDF-multiline — an attribute value with embedded newlines (a multiline
  // attribute, DXF 70 bit 32, OR a multiline constant / missing-ATTRIB default)
  // renders as STACKED TEXT lines, mirroring the typed oracle
  // `attdefFromEntity.ts renderAttributeValueLines`. The PDF text painter
  // (`textPainter.ts`) draws each run on ONE baseline and never advances on
  // `\n`, so a single un-split run would plot the whole string as one malformed
  // line. `lines.length === 1` for the overwhelming common single-line case →
  // exactly one run, byte-identical to the prior single-push.
  const lines = value.split(/\r\n|\r|\n/);
  for (let i = 0; i < lines.length; i++) {
    // PDF-multiline — line `i` steps by `i · lineStepWorld`, the WORLD-space
    // per-line offset `resolveAttrPlacement` formed in the LOCAL text frame and
    // mapped through the FULL block affine's linear part (so a non-uniform /
    // rotated INSERT spaces lines by the correct per-axis pitch, not the final
    // height along the final rotation). `i === 0` is a no-op for the common
    // single-line case → byte-identical to the prior single-push.
    const lineOffset = {
      x: i * placement.lineStepWorld.x,
      y: i * placement.lineStepWorld.y,
    };
    out.push({
      type: "text-run",
      entityId: state.parentId,
      // PDF-6.2a: the resolved OCG layer, computed by the caller. A MATCHED
      // ATTDEF template carries its OWN layer (DXF group 8) and files under
      // `resolveChildLayer(attdef.layer, insertLayer)` — the layer-0→INSERT
      // inheritance, exactly like a standalone block-child TEXT and the typed
      // oracle (`attdefFromEntity.ts` → `transformBlockChild`). An ORPHAN ATTRIB
      // has no template layer, so it files under the INSERT's resolved layer
      // (`state.insertLayer`; a nested INSERT on "0" contributes its
      // parent-inherited layer through the walk).
      layer,
      position: {
        x: placement.position.x + lineOffset.x,
        y: placement.position.y + lineOffset.y,
      },
      height: placement.height,
      rotation: placement.rotation,
      // PDF-F9 — the template widthFactor carries the effective transform's
      // |scaleX|/|scaleY| glyph aspect (see `resolveAttrPlacement`).
      widthFactor: style.widthFactor * placement.widthFactorScale,
      obliqueAngle: style.obliqueAngle,
      hJustification: style.hJustification,
      vJustification: style.vJustification,
      styleName,
      value: lines[i]!,
      color,
      ...(alpha !== undefined ? { alpha } : {}),
      // GitHub Codex review PR #558 round 2 (round-3 local read-only
      // follow-up P2, generalised by the round-9 GitHub bot follow-up P2)
      // — thread the attribute's full 2-bit handedness into the PDF glyph
      // run (`attributeHandedness`'s doc has the full rationale).
      // Undefined/false ⇒ no key, matching `TextRunPrimitive.mirrored`/
      // `.backward`'s golden-parity contract for every existing
      // (unmirrored/non-backward) run.
      ...(mirrored ? { mirrored: true } : {}),
      ...(backward ? { backward: true } : {}),
    });
  }
}

/**
 * PDF-F1 / F3 / F4 / F18 — resolve + emit one VARIABLE ATTDEF (group 70 bit 2
 * clear). Matches the ATTRIB row by tag + occurrence (F4); renders that row's
 * value, or the ATTDEF `defaultValue` (never blank) when no row matches (F3); an
 * explicit `overridePosition` is a WORLD placement guarded via `state.outer`
 * (F1), else the block-local template anchor rides `baseMatrix`.
 *
 * `isArrayCopy` (PDF-F18) — a dynamic-Array EXTRA copy (`${sourceId}#arr<n>`)
 * reuses the SOURCE ATTRIB row for its value / visibility / style overrides, but
 * its PLACEMENT must come from the COPY's OWN block-local template anchor (which
 * the Array action translated per copy — `block-evaluator/geometry.ts` moves the
 * ATTDEF's insertion + alignment points), NOT the source row's WORLD
 * `overridePosition` (a single world point that anchors only the BASE copy —
 * reusing it would pile every arrayed label onto the base). So an array copy is
 * ALWAYS block-local, ignoring the row's `overridePosition`; the authored
 * (non-`#arr`) base copy still honours it. Byte-identical for the non-array case
 * and for an array whose source row carries no `overridePosition`.
 */
export function emitVariableAttdef(
  out: CadPrimitive[],
  attdef: AttdefEntity,
  occurrenceIndex: number,
  isArrayCopy: boolean,
  state: AttrEmitContext,
  baseMatrix: Affine2D,
  suppressUnmatchedDefault = false,
): void {
  const attr = findAttrByTagOccurrence(
    state.attributes,
    attdef.tag,
    occurrenceIndex,
  );
  // GUARD (cycle / over-depth) — surface ONLY a LIVE matched ATTRIB value; an
  // UNMATCHED variable ATTDEF must NOT fall back to its template default here.
  // The pre-refactor post-recursion loop re-surfaced only actual
  // `child.attributes`, so a cyclic / over-depth block with a defaulted ATTDEF
  // and no matching row must not leak a spurious default label (@codex #480).
  // The unguarded walk keeps the F3 template-default fallback (`suppress` off).
  if (suppressUnmatchedDefault && attr === undefined) return;
  // EATTEDIT per-ATTRIB visibility override wins; else the ATTDEF's own bit.
  const invisible = attr?.invisible ?? attdefFlag(attdef, "invisible");
  if (invisible) return;
  // PDF-F11 / F12 — resolve the ATTDEF template's plot style (its OWN colour +
  // CTB/STB do-not-plot + screening alpha), regardless of whether a per-instance
  // ATTRIB matched (the ATTRIB carries no independent colour). A do-not-plot
  // template emits nothing.
  const style = resolveAttdefPlotStyle(attdef, state);
  if (style.skip) return;
  // PDF-F3 — a matched row renders its value verbatim (even an intentional "");
  // a genuinely ABSENT row falls back to the ATTDEF template default, never
  // blank (mirrors the typed `resolveAttributeValue`).
  const value =
    attr !== undefined
      ? attr.value
      : attdef.defaultValue || attdef.tag || "?";
  // PDF-F18 — an Array EXTRA copy ignores the source row's WORLD override (it
  // anchors only the base copy) and rides its OWN translated template anchor.
  const worldPlacement = !isArrayCopy && attr?.overridePosition !== undefined;
  const anchorLocal: Point2 = worldPlacement
    ? { x: attr!.overridePosition!.x, y: attr!.overridePosition!.y }
    : resolveTemplateAnchor(attdef);
  // PDF-F18 — an Array EXTRA copy ignores the source row's `overrideHeight` /
  // `overrideRotation` ONLY when they are WORLD-final scalars, i.e. coupled to a
  // world `overridePosition` (which anchors just the BASE copy). Reusing THOSE
  // under the block-LOCAL branch would let `resolveAttrPlacement` scale the
  // height by |scaleY| and add the INSERT rotation AGAIN, double-scaling /
  // double-rotating every arrayed label. But a value-only / EATTEDIT row that
  // carries a block-LOCAL `overrideHeight` / `overrideRotation` with NO
  // `overridePosition` rides the SAME block-local regime as the base copy — the
  // INSERT scale/rotation is applied ONCE — so an array copy MUST reuse it too,
  // else arrayed labels print at the TEMPLATE height/angle while the base copy
  // (and the non-array render/export paths) use the override (@codex #507). So
  // the discard is gated on the SOURCE row's world placement, not on
  // `isArrayCopy` alone. Byte-identical for the non-array case, for a
  // world-placed source row, and for a source row with no scalar override.
  const arrayCopyDropsWorldScalars =
    isArrayCopy && attr?.overridePosition !== undefined;
  const heightBase = worldPlacement
    ? attr!.overrideHeight ?? 2.5
    : arrayCopyDropsWorldScalars
      ? attdef.height ?? 2.5
      : attr?.overrideHeight ?? attdef.height ?? 2.5;
  const rotBase = worldPlacement
    ? attr!.overrideRotation ?? 0
    : arrayCopyDropsWorldScalars
      ? attdef.rotation ?? 0
      : attr?.overrideRotation ?? attdef.rotation ?? 0;
  // WORLD regime uses the DXF-group default style ("Standard"); block-local
  // follows the override-or-template chain (mirrors the typed `resolvePlacement`
  // + the export encoder's unset-override defaults).
  const styleName = worldPlacement
    ? attr!.overrideStyle ?? "Standard"
    : attr?.overrideStyle ?? attdef.styleName ?? "Standard";
  // GitHub Codex review PR #558 round 2 (round-3 local read-only follow-up
  // P2, corrected by the round-14 local read-only follow-up P2 — "Keep
  // array-copy attribute handedness") — gated on the SOURCE ROW's OWN
  // `overridePosition` presence (verbatim WORLD-vs-block-local identity),
  // NOT the placement-regime-adjusted `worldPlacement` variable above (which
  // `isArrayCopy` forces false even for a row that WAS WORLD-placed).
  // UNLIKE height/rotation (`arrayCopyDropsWorldScalars` just above — a
  // GENUINE double-transform risk if a WORLD scalar rides the array copy's
  // own block-local affine), generationFlags has NO block-affine
  // interaction to double-count — it is a discrete per-instance bit pattern
  // (MIRRTEXT/backward handedness) that only needs to know whether ITS ROW
  // is/was a WORLD row, so an array copy of a WORLD-placed row keeps that
  // row's OWN flags. A genuinely block-local row (`overridePosition` never
  // set) stays "out of MIRRTEXT's scope" — any stray `overrideGenerationFlags`
  // on it is ignored in favour of the ATTDEF template's flags, exactly as
  // round-3 established (`geometryTransforms.blockAttributeOverride.test.ts`'s
  // and this file's OWN sibling test: "a BLOCK-LOCAL... ATTRIB rides the
  // ATTDEF template's OWN generationFlags, not any overrideGenerationFlags").
  const generationFlags =
    attr?.overridePosition !== undefined
      ? attr!.overrideGenerationFlags ?? 0
      : attdef.generationFlags;
  const handedness = attributeHandedness(generationFlags);
  pushAttrTextRun(
    out,
    state,
    resolveAttrPlacement(
      worldPlacement,
      anchorLocal,
      heightBase,
      rotBase,
      baseMatrix,
      state,
      handedness.mirrored,
    ),
    value,
    styleName,
    attdefStyleFields(attdef),
    // A matched ATTDEF template carries its OWN OCG layer; file under it with
    // the layer-0→INSERT inheritance (mirrors the typed oracle + block TEXT).
    resolveChildLayer(attdef.layer, state.insertLayer),
    // PDF-F11 — the ATTDEF template's OWN resolved colour (not the INSERT's);
    // PDF-F12 — any CTB/STB screening alpha.
    style.color,
    style.alpha,
    handedness.mirrored,
    handedness.backward,
  );
}

/**
 * PDF-F2 — resolve + emit one CONSTANT ATTDEF (group 70 bit 2 set). A constant
 * has NO per-instance ATTRIB row: its baked `defaultValue` (verbatim, even "")
 * renders at every INSERT, at the block-local template anchor (Phase B of the
 * typed reference). An invisible constant renders nothing.
 */
export function emitConstantAttdef(
  out: CadPrimitive[],
  attdef: AttdefEntity,
  state: AttrEmitContext,
  baseMatrix: Affine2D,
): void {
  if (attdefFlag(attdef, "invisible")) return;
  // PDF-F11 / F12 — a constant ATTDEF is a template too: resolve its OWN plot
  // style (colour + do-not-plot + screening alpha). A do-not-plot constant
  // emits nothing.
  const style = resolveAttdefPlotStyle(attdef, state);
  if (style.skip) return;
  // GitHub Codex review PR #558 round 2 (round-3 local read-only follow-up
  // P2, generalised by the round-9 GitHub bot follow-up P2) — a CONSTANT
  // ATTDEF has no per-instance ATTRIB row (no WORLD override possible), so
  // its handedness is always the template's OWN `generationFlags`,
  // mirroring `attdefFromEntity.ts`'s block-local fallback.
  const handedness = attributeHandedness(attdef.generationFlags);
  pushAttrTextRun(
    out,
    state,
    resolveAttrPlacement(
      false,
      resolveTemplateAnchor(attdef),
      attdef.height ?? 2.5,
      attdef.rotation ?? 0,
      baseMatrix,
      state,
      handedness.mirrored,
    ),
    attdef.defaultValue,
    attdef.styleName ?? "Standard",
    attdefStyleFields(attdef),
    // A constant ATTDEF is a matched template too — file under its own layer.
    resolveChildLayer(attdef.layer, state.insertLayer),
    // PDF-F11 — the constant ATTDEF's OWN resolved colour; PDF-F12 — its alpha.
    style.color,
    style.alpha,
    handedness.mirrored,
    handedness.backward,
  );
}

/**
 * Emit one ORPHAN ATTRIB — a `attributes[]` row whose tag matched no ATTDEF
 * occurrence in this block definition (the ATTDEF was removed / renamed, or a
 * stray duplicate beyond the last same-tag ATTDEF, or the block itself is
 * missing). No template to inherit from, so left-justified defaults are used;
 * a WORLD `overridePosition` still rides `state.outer` (PDF-F1), the
 * no-override block-origin fallback rides `localFallbackMatrix` (the block's
 * `baseMatrix`, or `state.matrix` when the block definition is absent).
 */
export function emitOrphanAttribute(
  out: CadPrimitive[],
  attr: BlockInsertAttribute,
  state: AttrEmitContext,
  localFallbackMatrix: Affine2D,
): void {
  if (attr.invisible) return;
  const worldPlacement = attr.overridePosition !== undefined;
  const anchorLocal: Point2 = worldPlacement
    ? { x: attr.overridePosition!.x, y: attr.overridePosition!.y }
    : { x: 0, y: 0 };
  // GitHub Codex review PR #558 round 2 (round-3 local read-only follow-up
  // P2, generalised by the round-9 GitHub bot follow-up P2) — an orphan
  // ATTRIB (no ATTDEF template) ALWAYS carries its own
  // `overrideGenerationFlags`, mirroring `attdefFromEntity.ts`'s orphan
  // path (`orphanAttributeFromEntity`'s unconditional
  // `attr.overrideGenerationFlags ?? 0`).
  const handedness = attributeHandedness(attr.overrideGenerationFlags ?? 0);
  pushAttrTextRun(
    out,
    state,
    resolveAttrPlacement(
      worldPlacement,
      anchorLocal,
      attr.overrideHeight ?? 2.5,
      attr.overrideRotation ?? 0,
      localFallbackMatrix,
      state,
      handedness.mirrored,
    ),
    attr.value,
    attr.overrideStyle ?? "Standard",
    ORPHAN_STYLE_FIELDS,
    // An orphan ATTRIB has NO ATTDEF template to inherit a layer from (the
    // model carries no per-ATTRIB layer), so it files under the enclosing
    // INSERT's resolved layer — matching the typed orphan path
    // (`orphanAttributeFromEntity` → `identity.parentLayer`).
    state.insertLayer,
    // No ATTDEF template → inherit the enclosing INSERT's own resolved colour
    // (the typed orphan renders `color:{kind:"byBlock"}`, which resolves to the
    // same value). No CTB/STB alpha/skip: an orphan has no template ACI to look
    // up, matching the typed orphan path (which applies no plot-style override).
    attributeColor(state),
    undefined, // no CTB/STB alpha for an orphan (see above).
    handedness.mirrored,
    handedness.backward,
  );
}

/** Emit every `attributes[]` row as an orphan — used only when the block
 *  definition is missing (no ATTDEF templates to resolve against) so the
 *  INSERT's live attribute data is still surfaced rather than dropped. */
export function emitOrphanAttributes(
  out: CadPrimitive[],
  state: AttrEmitContext,
  localFallbackMatrix: Affine2D,
): void {
  for (const attr of state.attributes) {
    emitOrphanAttribute(out, attr, state, localFallbackMatrix);
  }
}
